import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Check, ChevronDown, Filter, Info, Plus, Search, X } from "lucide-react";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";
import { Tables } from "@/lib/supabase/supabaseTypes";
import fuzzysort from "fuzzysort";
import {
  equipmentFilter,
  Exercise,
  experienceFilter,
  muscleGroups,
  typeFilter,
} from "../exercises/exerciseColumns";
import { cn } from "@/lib/utils/ui";
import ExerciseDialog from "../exercises/exerciseDialog";
import { useExerciseDatabase } from "@/lib/queries/useExerciseDatabase";
import { useDraggable } from "@dnd-kit/core";
import { LibraryExerciseDragData } from "./workoutEditorDnd";
import { useWorkoutEditorWorkspace } from "./WorkoutEditorWorkspace";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import { DataTableFilterOptions } from "../dataTable/DataTableFilter";
import { CheckIcon } from "@radix-ui/react-icons";
import { SYSTEM_ORG_UUID } from "@/lib/supabase/supabaseClient";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Skeleton } from "../ui/skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../ui/empty";

interface ExerciseSelectListProps {
  exerciseSelected: (exercise: Tables<"exercises">) => void;
  onExerciseCreated: (exercise: Exercise) => void;
  className?: string;
  disabled?: boolean;
  enableDragToWorkout?: boolean;
}

const ExerciseSelectList: FunctionComponent<ExerciseSelectListProps> = ({
  exerciseSelected,
  onExerciseCreated,
  className,
  disabled,
  enableDragToWorkout = false,
}) => {
  const { exercises: data, isLoading } = useExerciseDatabase();
  const { activeDrag } = useWorkoutEditorWorkspace();

  const [searchValue, setSearchValue] = useState("");
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedExperienceLevels, setSelectedExperienceLevels] = useState<
    string[]
  >([]);
  const [hideTrainrrLibrary, setHideTrainrrLibrary] = useState(false);
  const [openExerciseDialog, setOpenExerciseDialog] = useState(false);
  const [exerciseToView, setExerciseToView] = useState<Exercise>();

  const scrollRef = useRef<HTMLDivElement>(null);
  const isLibraryDragActive = activeDrag?.type === "library-exercise";
  const exercises = useMemo(() => {
    const allExercises = (data as Exercise[] | undefined) ?? [];

    const filteredExercises = allExercises.filter((exercise) => {
      if (
        hideTrainrrLibrary &&
        exercise.org_id === SYSTEM_ORG_UUID
      ) {
        return false;
      }

      if (
        selectedMuscleGroups.length > 0 &&
        !exercise.primary_muscles.some((muscle) =>
          selectedMuscleGroups.includes(muscle),
        )
      ) {
        return false;
      }

      if (
        selectedEquipment.length > 0 &&
        !selectedEquipment.includes(exercise.equipment ?? "")
      ) {
        return false;
      }

      if (selectedTypes.length > 0 && !selectedTypes.includes(exercise.type ?? "")) {
        return false;
      }

      if (
        selectedExperienceLevels.length > 0 &&
        !selectedExperienceLevels.includes(exercise.experience ?? "")
      ) {
        return false;
      }

      return true;
    });

    if (!searchValue) {
      return filteredExercises;
    }

    return fuzzysort
      .go(searchValue, filteredExercises, { key: "name" })
      .map((result) => result.obj);
  }, [
    data,
    hideTrainrrLibrary,
    searchValue,
    selectedEquipment,
    selectedExperienceLevels,
    selectedMuscleGroups,
    selectedTypes,
  ]);
  const totalFilters =
    selectedMuscleGroups.length +
    selectedEquipment.length +
    selectedTypes.length +
    selectedExperienceLevels.length +
    (hideTrainrrLibrary ? 1 : 0);
  const hasActiveQuery = searchValue.trim().length > 0 || totalFilters > 0;

  const virtualizer = useVirtualizer({
    count: exercises?.length ?? 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0;

  useEffect(() => {
    const element = scrollRef.current;

    if (!element || !isLibraryDragActive) {
      return;
    }

    const preventScroll = (event: Event) => {
      event.preventDefault();
    };

    element.addEventListener("wheel", preventScroll, { passive: false });
    element.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      element.removeEventListener("wheel", preventScroll);
      element.removeEventListener("touchmove", preventScroll);
    };
  }, [isLibraryDragActive]);

  useEffect(() => {
    virtualizer.scrollToOffset(0);
  }, [
    hideTrainrrLibrary,
    searchValue,
    selectedEquipment,
    selectedExperienceLevels,
    selectedMuscleGroups,
    selectedTypes,
    virtualizer,
  ]);

  const resetFilters = () => {
    setSelectedMuscleGroups([]);
    setSelectedEquipment([]);
    setSelectedTypes([]);
    setSelectedExperienceLevels([]);
    setHideTrainrrLibrary(false);
  };

  return (
    <>
      <ExerciseDialog
        open={openExerciseDialog}
        onOpenChange={() => {
          setExerciseToView(undefined);
          setOpenExerciseDialog(false);
        }}
        onExerciseCreated={onExerciseCreated}
        exercise={exerciseToView}
      />
      <Card
        data-workout-library-scroll="true"
        className={cn(
          "sticky max-w-[400px] min-w-[350px] h-[80vh] top-6 flex-1 flex flex-col overflow-hidden",
          className,
        )}
      >
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <div className="px-2 pt-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-5 w-5 text-gray-400" />
                </span>
                <Input
                  placeholder="Search exercises"
                  className="pl-10"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                  }}
                />
              </div>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative h-10 w-10 shrink-0 p-0"
                    aria-label="Open exercise filters"
                  >
                    <Filter className="size-4" />
                    {totalFilters > 0 && (
                      <span className="absolute right-2 top-2 size-2 rounded-full bg-blue-500" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="flex flex-row items-center justify-between">
                    <h1 className="font-semibold">Filters</h1>
                    <Button variant="ghost" onClick={resetFilters}>
                      Clear filters
                    </Button>
                  </div>
                  <Separator className="my-4" />
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-row items-center justify-between px-2">
                      Hide Trainrr Library
                      <Switch
                        checked={hideTrainrrLibrary}
                        onCheckedChange={setHideTrainrrLibrary}
                      />
                    </div>
                    <ExerciseFilterSelect
                      title="Muscle groups"
                      options={muscleGroups}
                      selectedValues={selectedMuscleGroups}
                      onChange={setSelectedMuscleGroups}
                    />
                    <ExerciseFilterSelect
                      title="Equipment"
                      options={equipmentFilter}
                      selectedValues={selectedEquipment}
                      onChange={setSelectedEquipment}
                    />
                    <ExerciseFilterSelect
                      title="Type"
                      options={typeFilter}
                      selectedValues={selectedTypes}
                      onChange={setSelectedTypes}
                    />
                    <ExerciseFilterSelect
                      title="Experience"
                      options={experienceFilter}
                      selectedValues={selectedExperienceLevels}
                      onChange={setSelectedExperienceLevels}
                    />
                  </div>
                </PopoverContent>
              </Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 shrink-0 p-0"
                    onClick={() => setOpenExerciseDialog(true)}
                    aria-label="Create custom exercise"
                  >
                    <Plus className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Custom Exercise</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <Separator className="mt-4" />

          <div
            ref={scrollRef}
            data-workout-library-scroll="true"
            className={cn(
              "min-h-0 flex-1",
              isLibraryDragActive
                ? "overflow-y-hidden overscroll-none touch-none"
                : "overflow-y-auto",
            )}
          >
            {isLoading ? (
              <div className="space-y-1 p-2">
                {Array.from({ length: 8 }).map((_, index) => (
                  <ExerciseRowSkeleton key={index} />
                ))}
              </div>
            ) : exercises.length === 0 ? (
              <div className="flex h-full items-center justify-center p-4">
                <Empty className="border py-10">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Search className="size-5" />
                    </EmptyMedia>
                    <EmptyTitle>
                      {hasActiveQuery
                        ? "No matching exercises"
                        : "No exercises yet"}
                    </EmptyTitle>
                    <EmptyDescription>
                      {hasActiveQuery
                        ? "Try adjusting your search or filters, or create a custom exercise."
                        : "Your library is empty. Create a custom exercise to get started."}
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button onClick={() => setOpenExerciseDialog(true)}>
                      <Plus className="size-4" />
                      Custom Exercise
                    </Button>
                  </EmptyContent>
                </Empty>
              </div>
            ) : (
              <>
                {paddingTop > 0 && <div style={{ height: paddingTop }} />}
                {virtualItems.map((virtualItem) => {
                  const exercise = exercises[virtualItem.index];
                  return (
                    <ExerciseRow
                      key={virtualItem.key}
                      exercise={exercise}
                      disabled={disabled}
                      enableDragToWorkout={enableDragToWorkout}
                      exerciseSelected={exerciseSelected}
                      onInfoSelected={(exercise) => {
                        setExerciseToView(exercise);
                        setOpenExerciseDialog(true);
                      }}
                    />
                  );
                })}
                {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

interface ExerciseFilterSelectProps {
  title: string;
  options: DataTableFilterOptions[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}

const ExerciseFilterSelect: FunctionComponent<ExerciseFilterSelectProps> = ({
  title,
  options,
  selectedValues,
  onChange,
}) => {
  const selectedValueSet = new Set(selectedValues);

  return (
    <Popover modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className="min-w-40 h-fit justify-between hover:bg-inherit hover:border-primary"
        >
          {selectedValues.length === 0 ? (
            <span className="opacity-50 w-full text-start">Select {title}</span>
          ) : (
            <div className="flex w-full flex-wrap gap-1">
              {options
                .filter((option) => selectedValueSet.has(option.value))
                .map((option) => (
                  <Badge
                    variant="secondary"
                    key={option.value}
                    className="rounded-sm px-1 font-normal hover:cursor-default"
                  >
                    {option.label}
                    <span
                      className="hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(
                          selectedValues.filter((value) => value !== option.value),
                        );
                      }}
                    >
                      <X className="opacity-50 size-0.5 ml-1" />
                    </span>
                  </Badge>
                ))}
            </div>
          )}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValueSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        onChange(
                          selectedValues.filter((value) => value !== option.value),
                        );
                        return;
                      }

                      onChange([...selectedValues, option.value]);
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface ExerciseRowProps {
  exercise: Exercise;
  disabled?: boolean;
  enableDragToWorkout?: boolean;
  exerciseSelected: (exercise: Exercise) => void;
  onInfoSelected: (exercise: Exercise) => void;
}

const ExerciseRow: FunctionComponent<ExerciseRowProps> = ({
  exercise,
  disabled,
  enableDragToWorkout = false,
  exerciseSelected,
  onInfoSelected,
}) => {
  const [showTick, setShowTick] = useState(false);
  const primaryMuscle =
    exercise.primary_muscles.length > 0
      ? exercise.primary_muscles[0].charAt(0).toUpperCase() +
        exercise.primary_muscles[0].slice(1).toLowerCase()
      : "";
  const equipmentLabel = exercise.equipment
    ? equipmentFilter.find((option) => option.value === exercise.equipment)
        ?.label ?? exercise.equipment
    : "";
  const subtitle = [primaryMuscle, equipmentLabel].filter(Boolean).join(" - ");
  const dragData: LibraryExerciseDragData = {
    type: "library-exercise",
    exerciseId: exercise.id,
    exerciseName: exercise.name,
    exercise,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `library-exercise-${exercise.id}`,
    data: dragData,
    disabled: disabled || !enableDragToWorkout,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "group flex flex-row items-center gap-4 p-2 px-3 hover:bg-secondary",
        disabled
          ? "cursor-default"
          : enableDragToWorkout
            ? "cursor-grab select-none touch-manipulation active:cursor-grabbing"
            : "cursor-pointer",
        isDragging && "opacity-40",
      )}
      onClick={() => {
        if (disabled) return;
        exerciseSelected(exercise);
        setShowTick(true);
        setTimeout(() => {
          setShowTick(false);
        }, 650);
      }}
    >
      <Button
        size={"icon"}
        disabled={disabled}
        variant={disabled ? "ghost" : "ghost"}
        className="rounded-full p-0 size-7 opacity-90 flex-shrink-0"
      >
        {showTick ? <Check className="size-4" /> : <Plus className="size-4" />}
      </Button>
      <div>
        <h2 className="text-lg font-medium">{exercise.name}</h2>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      <div className="relative flex-grow">
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size={"icon"}
            variant="ghost"
            className="rounded-full hover:border"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onInfoSelected(exercise);
            }}
          >
            <Info className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ExerciseRowSkeleton: FunctionComponent = () => {
  return (
    <div className="flex flex-row items-center gap-4 p-2 px-3">
      <Skeleton className="size-7 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="size-8 shrink-0 rounded-full opacity-60" />
    </div>
  );
};

export default ExerciseSelectList;
